/**
 * The kitchen documents of handover rev7 §11.6, as blocks.
 *
 * §11.6 asks for every kitchen output in two forms: "table-based HTML email
 * (Raleway with Arial fallback, 680 px) **plus the A4 print view**". They are
 * two renders of one document, so they are built from one reading —
 * `public.kitchen_doc` for the six that are about an order and
 * `public.kitchen_day_doc` for the two that are about a day — which travels on
 * the payload as `digest.document`. If the email built its own reading out of
 * the order payload, the kitchen would hold two sheets with the same name and
 * different figures on them.
 *
 * Four rules, the same four the A4 sheet holds itself to:
 *
 *  - **No prices.** There are none in the reading, so there are none here.
 *  - **Out of the snapshot** (§19.2 principle 1) — the database did that.
 *  - **Allergens as code and full name**, `8 diófélék`, with §7.3's
 *    verification state beside them. Example data never reads as verified.
 *  - **A gap prints as a gap.** §7.3's interim catalogue has no station, no
 *    lead time and no *Adag / doboz* for any dish, and a document that printed
 *    a zero would be lying about it.
 *
 * Nothing here reads a clock, a database or an environment: a kitchen block is
 * a pure function of the document, which is what makes the whole set testable
 * without a stack.
 */

import type { Block, Cell } from './layout.ts'
import { article, date, dateTime, km, n, qty, time } from './format.ts'
/* Run 19 §8: the one place the collection warning is worded, shared with
   the page, the emails and the console so none of them can word it
   differently. */
import { earlyPickupKitchenNotice } from '../../engine/src/eventTime.ts'
import { ALLERGENS_BEING_VERIFIED } from './osszesites.ts'
import type { EmailPayload } from './payload.ts'

/** What a field the catalogue has no answer for prints as (§7.3). */
export const MISSING = '— nincs adat'

export interface DocAllergen {
  code: number | string
  name_hu?: string | null
}

export interface DocLine {
  dish_id: string
  name_hu: string | null
  station: string | null
  is_gf_portion: boolean
  /**
   * Run 17 item B5(b): how many of `ordered_portions` belong to the
   * gluten-free guests, where this dish is on the menu and carries their
   * share. **Assumption A5:** the kitchen reads that share as a line of its
   * own, to pack in its own labelled gluten-free box.
   */
  gf_portions?: number | null
  is_drink: boolean
  gluten_free_eligible: boolean | null
  ordered_portions: number
  unit: string | null
  production_units: number | null
  production_unit: string | null
  portions_per_box: number | null
  production_day: string | null
  boxes: number | null
  allergens: DocAllergen[] | null
  allergen_status: string | null
  allergen_test_marked: boolean | null
}

export interface DocChange {
  dish_id: string
  name_hu: string | null
  unit: string | null
  is_gf_portion: boolean | null
  was: number | null
  now: number | null
  delta: number
  change: 'added' | 'removed' | 'changed'
}

export interface KitchenOrderDocument {
  kind: 'K1' | 'K2' | 'K3' | 'K4' | 'K6' | 'K8'
  generated_at: string
  superseded: boolean
  version: number
  current_version: number
  based_on_version: number | null
  version_reason: string | null
  bands: string[]
  changes: DocChange[]
  order: {
    ref: string
    status: string
    event_name: string | null
    event_date: string
    event_start: string | null
    occasion_label: string | null
    /** Run 19 §4: the meal note the occasion carries, beside its name. */
    meal_note?: string | null
    /**
     * Run 19 §17: *Szállításra összekészítve*, as `public.order_ready_by`
     * works it out — the override the catering role set, else the slot
     * start less `dispatch_lead`. An ISO instant, or null where the order
     * has no slot to anchor on.
     */
    ready_by?: string | null
    /** True where somebody set it by hand rather than taking the default. */
    ready_by_overridden?: boolean
    /** Run 19 §18: how the food leaves the kitchen, in words. */
    packing_label?: string | null
    packing_mode?: string | null
    /** Run 19 §16: the cups and the machines, for the packing list. */
    coffee_cups?: number | null
    coffee_machines?: number | null
    guests: number
    gf_guests: number
    fulfilment: 'delivery' | 'pickup'
    slot_label: string | null
    /**
     * Run 19 §8: the day the food changes hands — the event date for a
     * delivery and an ordinary collection, the business day before for a
     * weekend event (§8.1). Optional, because a document rendered before
     * this run has none, and then no warning is drawn.
     */
    fulfilment_date?: string | null
    /** §12's `pickup_to`, so the warning can name the time. */
    pickup_to?: string | null
    zone: string | null
    /** Specification §3.7: the distance beside the zone. */
    distance_km?: number | string | null
    delivery_address: Record<string, string> | null
    customer_name: string | null
    customer_company: string | null
    customer_phone: string | null
    dietary_note: string | null
    labels_blocked: boolean
    is_short_notice: boolean
    is_late: boolean
  }
  lines: DocLine[]
  totals: {
    portions: number
    gf_portions: number
    drinks: number
    boxes: number
    boxes_unknown: number
  }
  dietary_signoff: {
    decision: string
    resolution: string
    signed_by: { name?: string; email?: string }
    at: string
  } | null
}

export interface ProductionOrderRow {
  ref: string
  event_date: string
  event_start: string | null
  guests: number
  portions: number
  production_units: number | null
  is_gf_portion: boolean
  unit: string | null
}

export interface ProductionDish {
  dish_id: string
  name_hu: string | null
  unit: string | null
  production_unit: string | null
  portion_definition: string | null
  is_drink: boolean
  allergens: DocAllergen[] | null
  allergen_status: string | null
  allergen_test_marked: boolean | null
  portions: number
  gf_portions: number
  production_units: number | null
  orders: ProductionOrderRow[]
}

export interface KitchenProductionDocument {
  kind: 'K5'
  for_date: string
  generated_at: string
  bands: string[]
  stations: { station: string | null; portions: number; gf_portions: number; dishes: ProductionDish[] }[]
  orders: {
    ref: string
    event_date: string
    event_start: string | null
    guests: number
    gf_guests: number
    fulfilment: string
    slot_label: string | null
    status: string
    dietary_note: string | null
    labels_blocked: boolean
    bands: string[]
  }[]
  totals: {
    portions: number
    gf_portions: number
    drinks: number
    dishes: number
    orders: number
    stations: number
  }
}

export interface ForecastDay {
  date: string
  holiday_name: string | null
  kitchen_closed: boolean
  is_clash_date: boolean
  note: string | null
  portion_cap: number | null
  max_orders_per_day: number | null
  max_covers_per_day: number | null
  soft_threshold: number | null
  committed_portions: number
  committed_orders: number
  committed_covers: number
  distinct_savoury: number
  distinct_sweet: number
  overridden_orders: number
}

export interface ForecastDish {
  dish_id: string
  name_hu: string | null
  station: string | null
  unit: string | null
  vegleges: number
  visszaigazolt: number
  fuggo: number
  orders: number
  previous: { vegleges: number; visszaigazolt: number; fuggo: number } | null
  changed: boolean
}

export interface KitchenForecastDocument {
  kind: 'K7'
  for_date: string
  until: string
  forecast_bd: number
  generated_at: string
  previous_for_date: string | null
  bands: string[]
  days: ForecastDay[]
  dishes: ForecastDish[]
  gone: ForecastDish[]
  totals: {
    dishes: number
    days: number
    vegleges: number
    visszaigazolt: number
    fuggo: number
    orders: number
    covers: number
  }
}

export type KitchenDocument =
  | KitchenOrderDocument
  | KitchenProductionDocument
  | KitchenForecastDocument

/**
 * The document on the payload, or null.
 *
 * Null is a real case and not only a defensive one: an outbox row written
 * before this stage carries no document, and the preview page renders a
 * sample. Every template that reads one says so in words when it is missing
 * rather than printing an empty table.
 */
export function kitchenDocument(payload: EmailPayload): KitchenDocument | null {
  const document = payload.digest?.['document']
  if (document === null || document === undefined || typeof document !== 'object') return null
  const kind = (document as { kind?: unknown }).kind
  if (typeof kind !== 'string') return null
  return document as KitchenDocument
}

export const DOCUMENT_MISSING =
  'A konyhai dokumentum nem olvasható ehhez az értesítéshez. A nyomtatási nézet a rendelés ' +
  'oldalán nyitható meg (§11.6).'

/* ------------------------------------------------------------------ *
 * Shared pieces
 * ------------------------------------------------------------------ */

/** §11.6: allergens always as code and full name, with §7.3's verification
 *  state beside them. The same spelling as the Összesítés uses, over the
 *  kitchen document's own line shape. */
export function docAllergens(line: {
  allergens: DocAllergen[] | null
  allergen_status: string | null
  allergen_test_marked: boolean | null
}): string {
  const parts = (line.allergens ?? []).map((allergen) =>
    allergen.name_hu ? `${allergen.code} ${allergen.name_hu}` : String(allergen.code),
  )
  const verified = line.allergen_status === 'verified' && line.allergen_test_marked !== true
  // Only a verified dish prints a plain list, and only a verified dish with
  // an empty list prints *nincs*. Anything else opens with *Allergének
  // ellenőrzés alatt* (Barna, 10 October 2026). A kitchen document keeps
  // the unverified list after it, marked as such, because the kitchen
  // judges dietary requests by it; the customer sees the phrase alone.
  if (!verified) {
    return parts.length === 0
      ? ALLERGENS_BEING_VERIFIED
      : `${ALLERGENS_BEING_VERIFIED} — nem ellenőrzött lista: ${parts.join(' · ')}`
  }

  return parts.length === 0 ? 'allergének: nincs' : `allergének: ${parts.join(' · ')}`
}

/** §7.3: one line under the table, when any dish on it is unverified. Never
 *  omitted on a document whose data is example data. */
export function docAllergenCaveat(
  lines: { allergen_status: string | null; allergen_test_marked: boolean | null }[],
): Block | null {
  const unverified = lines.some(
    (line) => line.allergen_status !== 'verified' || line.allergen_test_marked === true,
  )
  return unverified
    ? {
        kind: 'note',
        text:
          'Az allergénadatok ELLENŐRZÉSRE VÁRNAK. Példaadatok, nem a konyha aláírt listája (§7.3).',
      }
    : null
}

const BAND_TONE: Record<string, 'warn' | 'stop'> = {
  FELÜLBÍRÁLAT: 'warn',
  'ÉTRENDI MEGJEGYZÉS': 'warn',
  'RÖVID HATÁRIDŐ': 'warn',
  'KÉSŐI RENDELÉS': 'warn',
  MÓDOSÍTVA: 'stop',
  TÖRÖLVE: 'stop',
  'CÍMKÉZÉS BLOKKOLVA': 'stop',
}

/** §11.6's alert bands, in the order the document lists them. The tone is the
 *  only thing decided here: a band that stops production is read differently
 *  from one that asks for care. */
export function docBands(bands: string[], superseded = false, version = 0, current = 0): Block[] {
  const blocks: Block[] = []
  if (superseded) {
    // DR-20: the article is the numeral's own — *az 1.*, *az 5.*, *a 2.* —
    // and the second clause keeps its noun rather than trailing off.
    blocks.push({
      kind: 'band',
      text:
        `ÉRVÉNYTELEN — ez ${article(version)} ${version}. verzió, ` +
        `${article(current)} ${current}. verzió van érvényben`,
      tone: 'stop',
    })
  }
  for (const band of bands) {
    blocks.push({ kind: 'band', text: band, tone: BAND_TONE[band] ?? 'warn' })
  }
  return blocks
}

const asDate = (value: string | null): string => (value === null ? MISSING : date(value, true))

/** The header block of §11.6: "reference, version, date, start, fulfilment,
 *  guests, gluten-free guests, zone, occasion". */
export function docHeader(doc: KitchenOrderDocument): Block {
  const o = doc.order
  const rows: [string, string][] = [
    ['Rendelés', o.ref],
    ['Verzió', `${doc.version}.`],
    ['Rendezvény', asDate(o.event_date)],
    ['Kezdés', o.event_start === null ? MISSING : time(o.event_start)],
    [
      'Teljesítés',
      o.fulfilment === 'pickup'
        ? `Személyes átvétel${o.slot_label ? ` · ${o.slot_label}` : ''}`
        // *A zóna* reads as the definite article; *A jelű zóna* does not.
        // One spelling, the Összesítés's (§5.11).
        : `Kiszállítás${o.slot_label ? ` · ${o.slot_label}` : ''}${o.zone ? ` · ${o.zone} jelű zóna` : ''}${km(o.distance_km) === '' ? '' : ` · ${km(o.distance_km)}`}`,
    ],
    [
      'Vendégek',
      `${n(o.guests)} fő${o.gf_guests > 0 ? ` · ebből ${n(o.gf_guests)} gluténmentes` : ''}`,
    ],
    /* Run 19 §4: the occasion name and its meal note travel together,
       here as on every other surface. `public.meal_replacement_note` is the
       one source; this document holds no copy of the words. */
    [
      'Alkalom',
      o.occasion_label === null
        ? MISSING
        : `${o.occasion_label}${o.meal_note ? ` — ${o.meal_note}` : ''}`,
    ],
  ]
  /* Run 19 §17 (EXPERT, Balázs 20261016): *Szállításra összekészítve*, the
     time the kitchen has to have the order ready. The default is the slot
     start less `dispatch_lead`; a time somebody set by hand says so, so a
     reprinted sheet can be told from the one the default produced. Never
     shown to a customer (§17). */
  if (o.ready_by !== undefined && o.ready_by !== null) {
    rows.push([
      'Szállításra összekészítve',
      `${dateTime(o.ready_by)}${o.ready_by_overridden === true ? ' · kézzel beállítva' : ''}`,
    ])
  }
  /* Run 19 §18 (EXPERT, Balázs 20261016): how the food leaves the kitchen,
     which follows the staff answer on every occasion. */
  if (o.packing_label !== undefined && o.packing_label !== null && o.packing_label !== '') {
    rows.push(['Csomagolás', o.packing_label])
  }
  /* Run 19 §16: the cups and the machines, so the packing list knows what
     equipment goes out with the food. */
  if (typeof o.coffee_cups === 'number' && o.coffee_cups > 0) {
    rows.push([
      'Kávé',
      `${n(o.coffee_cups)} csésze${
        typeof o.coffee_machines === 'number' && o.coffee_machines > 0
          ? ` · ${n(o.coffee_machines)} kávégép`
          : ''
      }`,
    ])
  }
  /* Run 19 §8: a collection that is not on the event date is the costliest
     thing on this sheet to misread, so it names the **production
     deadline** rather than repeating the event date. */
  const earlyPickup =
    o.fulfilment === 'pickup'
      ? earlyPickupKitchenNotice('hu', {
          pickupDate: o.fulfilment_date ?? null,
          eventDate: o.event_date,
          pickupTo: o.pickup_to ?? null,
        })
      : null
  if (earlyPickup !== null) rows.push(['Figyelem', earlyPickup])
  return { kind: 'facts', rows }
}

/** §11.6: "one row per dish with station, ordered portions, production units,
 *  production day, gluten-free eligibility, allergens". */
export function docLines(doc: KitchenOrderDocument): Block {
  return {
    kind: 'table',
    head: [
      'Fogás',
      'Állomás',
      { text: 'Adag', align: 'right' },
      { text: 'Gyártási egység', align: 'right' },
      'Gyártási nap',
      'Gluténmentes',
      'Allergének',
    ],
    rows: doc.lines.flatMap((line): Cell[][] => {
      /* Run 17 item B5(b), assumption A5: where a dish on the menu carries
         the gluten-free guests' share, the kitchen reads the share as its
         own line — what to pack in its own labelled gluten-free box — and
         the dish's own line is what is left. One batch, two packing lines,
         which is what the labels and the boxes of §11.6 need. */
      const share = line.is_gf_portion ? 0 : Math.max(line.gf_portions ?? 0, 0)
      const rest = Math.max(line.ordered_portions - share, 0)
      const row = (portions: number, glutenFree: boolean): Cell[] => [
        { text: line.name_hu ?? line.dish_id },
        { text: line.station ?? MISSING, muted: line.station === null },
        { text: qty(portions, line.unit ?? 'db'), align: 'right' },
        {
          /* The production units are the dish's own batch and belong on the
             line that is the batch, never split between two packing lines. */
          text: glutenFree
            ? '—'
            : line.production_units === null
              ? MISSING
              : `${n(line.production_units)} ${line.production_unit ?? ''}`.trim(),
          align: 'right',
          muted: !glutenFree && line.production_units === null,
        },
        { text: line.production_day === null ? MISSING : date(line.production_day, true),
          muted: line.production_day === null },
        {
          text: glutenFree
            ? 'GLUTÉNMENTES — külön doboz'
            : line.gluten_free_eligible === true
              ? 'alkalmas'
              : '—',
          strong: glutenFree,
        },
        { text: docAllergens(line), muted: true },
      ]
      if (share <= 0) return [row(line.ordered_portions, line.is_gf_portion)]
      if (rest <= 0) return [row(share, true)]
      return [row(rest, false), row(share, true)]
    }),
  }
}

/** The totals line of §11.6, with no price in it. */
export function docTotals(doc: KitchenOrderDocument): Block {
  const t = doc.totals
  const parts = [`Összesen ${n(t.portions)} adag`]
  if (t.gf_portions > 0) parts.push(`ebből ${n(t.gf_portions)} gluténmentes, külön dobozban`)
  if (t.drinks > 0) parts.push(`ebből ${n(t.drinks)} ital`)
  return { kind: 'facts', rows: [['Összesen', parts.join(' · ')]] }
}

/** §11.6: "dietary note with the signatory's resolution". A note with no
 *  resolution says so: §11.4 does not let the review close without one. */
export function docDietary(doc: KitchenOrderDocument): Block[] {
  if (doc.order.dietary_note === null || doc.order.dietary_note === '') return []
  const blocks: Block[] = [
    { kind: 'heading', text: 'Étrendi megjegyzés' },
    { kind: 'paragraph', text: doc.order.dietary_note },
  ]
  if (doc.dietary_signoff === null) {
    blocks.push({
      kind: 'note',
      text:
        'Aláírás még nincs. A konyhai dokumentum az allergén aláíró döntése nélkül nem teljes ' +
        '(§11.4).',
    })
  } else {
    blocks.push({
      kind: 'facts',
      rows: [
        [
          doc.dietary_signoff.decision === 'signed' ? 'Jóváhagyva' : 'Elutasítva',
          `${doc.dietary_signoff.resolution} · ${doc.dietary_signoff.signed_by?.name ?? ''}`,
        ],
      ],
    })
  }
  return blocks
}

/** §11.6: "delivery block". */
export function docDelivery(doc: KitchenOrderDocument): Block[] {
  const o = doc.order
  const address =
    o.fulfilment === 'pickup'
      ? 'Személyes átvétel a Planteen konyhájában.'
      : [
          o.delivery_address?.['postcode'],
          o.delivery_address?.['town'],
          o.delivery_address?.['street_and_number'],
          o.delivery_address?.['floor_and_door'],
        ]
          .filter(Boolean)
          .join(' ') || MISSING
  return [
    { kind: 'heading', text: o.fulfilment === 'pickup' ? 'Átvétel' : 'Kiszállítás' },
    {
      kind: 'facts',
      rows: [
        ['Cím', address],
        ['Idősáv', o.slot_label ?? MISSING],
        ['Telefon', o.customer_phone ?? MISSING],
      ],
    },
  ]
}

const CHANGE_LABEL: Record<string, string> = {
  added: 'új fogás',
  removed: 'elmarad',
  changed: 'megváltozott',
}

/** §11.6 K3: what moved against the version the kitchen already holds, so
 *  nobody has to diff two printouts by eye. */
export function docChanges(doc: KitchenOrderDocument): Block[] {
  if (doc.changes.length === 0) {
    return [
      {
        kind: 'note',
        text: 'A tételek nem változtak; a módosítás a rendelés más adatát érintette.',
      },
    ]
  }
  return [
    { kind: 'heading', text: 'Ami változott' },
    {
      kind: 'table',
      // *Korábban · Most*, not *Eddig · Mostantól*: the console's day view
      // already uses *Eddig* for the end of a date range, and one word for
      // two things in one application is one word too few.
      head: [
        'Fogás',
        'Változás',
        { text: 'Korábban', align: 'right' },
        { text: 'Most', align: 'right' },
        { text: 'Különbség', align: 'right' },
      ],
      rows: doc.changes.map((change): Cell[] => [
        {
          text:
            (change.name_hu ?? change.dish_id) +
            (change.is_gf_portion === true ? ' — gluténmentes, külön doboz' : ''),
        },
        { text: CHANGE_LABEL[change.change] ?? change.change },
        { text: change.was === null ? '—' : qty(change.was, change.unit ?? 'db'), align: 'right' },
        { text: change.now === null ? '—' : qty(change.now, change.unit ?? 'db'), align: 'right' },
        {
          text: `${change.delta > 0 ? '+' : ''}${qty(change.delta, change.unit ?? 'db')}`,
          align: 'right',
          strong: true,
        },
      ]),
    },
  ]
}

/* ------------------------------------------------------------------ *
 * K5 — the production reminder
 * ------------------------------------------------------------------ */

/** §11.6 K5: "everything to make on one production day, grouped by station
 *  and dish, split by order". One table per station; a dish row, then its
 *  orders indented under it. */
export function productionBlocks(doc: KitchenProductionDocument): Block[] {
  if (doc.stations.length === 0) {
    return [{ kind: 'note', text: 'Ezen a gyártási napon nincs gyártandó tétel.' }]
  }

  const blocks: Block[] = []
  for (const station of doc.stations) {
    blocks.push({
      kind: 'heading',
      text: station.station === null ? `Állomás ${MISSING}` : station.station,
    })
    const rows: Cell[][] = []
    for (const dish of station.dishes) {
      rows.push([
        { text: dish.name_hu ?? dish.dish_id, strong: true },
        { text: qty(dish.portions, dish.unit ?? 'db'), align: 'right', strong: true },
        {
          text:
            dish.production_units === null
              ? MISSING
              : `${n(dish.production_units)} ${dish.production_unit ?? ''}`.trim(),
          align: 'right',
          muted: dish.production_units === null,
        },
        { text: docAllergens(dish), muted: true },
      ])
      for (const order of dish.orders) {
        rows.push([
          {
            text: `${order.ref} · ${date(order.event_date, true)}${
              order.is_gf_portion ? ' · GLUTÉNMENTES, külön doboz' : ''
            }`,
            indent: true,
            muted: true,
          },
          { text: qty(order.portions, order.unit ?? 'db'), align: 'right', muted: true },
          { text: '', align: 'right' },
          { text: '', muted: true },
        ])
      }
    }
    blocks.push({
      kind: 'table',
      head: [
        'Fogás és rendelés',
        { text: 'Adag', align: 'right' },
        { text: 'Gyártási egység', align: 'right' },
        'Allergének',
      ],
      rows,
    })
  }

  blocks.push({
    kind: 'facts',
    rows: [
      [
        'Összesen',
        `${n(doc.totals.portions)} adag · ${n(doc.totals.dishes)} fogás · ` +
          `${n(doc.totals.orders)} rendelés`,
      ],
      ...(doc.totals.gf_portions > 0
        ? ([['Ebből gluténmentes', `${n(doc.totals.gf_portions)} adag, külön dobozban`]] as [
            string,
            string,
          ][])
        : []),
    ],
  })

  blocks.push({ kind: 'heading', text: 'A nap rendelései' })
  blocks.push({
    kind: 'table',
    head: ['Rendelés', 'Rendezvény', { text: 'Vendégek', align: 'right' }, 'Jelzés'],
    rows: doc.orders.map((order): Cell[] => [
      { text: order.ref },
      { text: `${date(order.event_date, true)}${order.event_start ? ` · ${time(order.event_start)}` : ''}` },
      { text: `${n(order.guests)} fő`, align: 'right' },
      { text: order.bands.length === 0 ? '—' : order.bands.join(' · '), muted: true },
    ]),
  })

  const caveat = docAllergenCaveat(doc.stations.flatMap((station) => station.dishes))
  if (caveat !== null) blocks.push(caveat)
  return blocks
}

/* ------------------------------------------------------------------ *
 * K7 — the forecast and the kitchen calendar
 * ------------------------------------------------------------------ */

const against = (committed: number, limit: number | null): string =>
  limit === null ? `${n(committed)} / ${MISSING}` : `${n(committed)} / ${n(limit)}`

/** §11.6 K7: the calendar page, then the dish-level forecast in three
 *  columns, with what moved since the previous forecast marked. */
export function forecastBlocks(doc: KitchenForecastDocument): Block[] {
  const blocks: Block[] = [
    // The document's own bands, so this render and the A4 sheet cannot show
    // different front pages (§11.11.5 reaches the calendar page too).
    ...docBands(doc.bands ?? []),
    { kind: 'heading', text: 'Konyhai naptár' },
    {
      kind: 'table',
      head: [
        'Nap',
        { text: 'Rendelés', align: 'right' },
        { text: 'Vendég', align: 'right' },
        { text: 'Adag', align: 'right' },
        { text: 'Sós', align: 'right' },
        { text: 'Édes', align: 'right' },
        'Megjegyzés',
      ],
      rows: doc.days.map((day): Cell[] => {
        const notes: string[] = []
        if (day.kitchen_closed) notes.push('KONYHA ZÁRVA')
        if (day.is_clash_date) notes.push('ÜTKÖZŐ NAP')
        if (day.holiday_name) notes.push(day.holiday_name)
        if (day.overridden_orders > 0) notes.push('FELÜLBÍRÁLAT')
        if (
          day.soft_threshold !== null &&
          day.committed_portions > day.soft_threshold
        ) {
          notes.push('kapacitásküszöb felett')
        }
        if (day.note) notes.push(day.note)
        return [
          { text: date(day.date, true) },
          { text: against(day.committed_orders, day.max_orders_per_day), align: 'right' },
          { text: against(day.committed_covers, day.max_covers_per_day), align: 'right' },
          { text: against(day.committed_portions, day.portion_cap), align: 'right' },
          { text: n(day.distinct_savoury), align: 'right' },
          { text: n(day.distinct_sweet), align: 'right' },
          { text: notes.join(' · ') || '—', muted: true },
        ]
      }),
    },
    {
      kind: 'note',
      // §13 B43 is open and nothing enforces it. The two columns make it
      // visible, and the note says that is all they do.
      text:
        'A Sós és az Édes oszlop a napra eső rendelések különböző fogásainak számát mutatja. ' +
        'Tájékoztató adat: napi fogásszámra nincs érvényben korlát (B43).',
    },
    { kind: 'heading', text: 'Fogásonkénti előrejelzés' },
  ]

  if (doc.dishes.length === 0) {
    blocks.push({ kind: 'note', text: 'Ebben az időszakban nincs rendelés.' })
  } else {
    blocks.push({
      kind: 'table',
      head: [
        'Fogás',
        { text: 'Végleges', align: 'right' },
        { text: 'Visszaigazolt', align: 'right' },
        { text: 'Függő', align: 'right' },
        'Változás',
      ],
      rows: doc.dishes.map((dish): Cell[] => [
        { text: dish.name_hu ?? dish.dish_id },
        { text: n(dish.vegleges), align: 'right' },
        { text: n(dish.visszaigazolt), align: 'right' },
        { text: n(dish.fuggo), align: 'right' },
        { text: changeNote(dish, doc.previous_for_date), muted: true, strong: dish.changed },
      ]),
    })
  }

  if (doc.gone.length > 0) {
    blocks.push({ kind: 'heading', text: 'Lekerült az előrejelzésről' })
    blocks.push({
      kind: 'list',
      items: doc.gone.map(
        (dish) =>
          `${dish.name_hu ?? dish.dish_id} — a legutóbbi előrejelzésen még szerepelt, ` +
          'most már nem szerepel egyetlen rendelésen sem',
      ),
    })
  }

  blocks.push({
    kind: 'facts',
    rows: [
      ['Időszak', `${date(doc.for_date, true)} – ${date(doc.until, true)}`],
      ['Munkanapok', `${n(doc.totals.days)} munkanap`],
      [
        'Összesen',
        `végleges ${n(doc.totals.vegleges)} · visszaigazolt ${n(doc.totals.visszaigazolt)} · ` +
          `függő ${n(doc.totals.fuggo)} adag`,
      ],
      [
        'Előző előrejelzés',
        doc.previous_for_date === null
          ? 'nincs — ez az első, így nincs mihez hasonlítani'
          : date(doc.previous_for_date, true),
      ],
    ],
  })

  return blocks
}

function changeNote(dish: ForecastDish, previousDate: string | null): string {
  if (previousDate === null) return '—'
  if (!dish.changed) return 'változatlan'
  if (dish.previous === null) return 'új a legutóbbi előrejelzés óta'
  const delta =
    dish.vegleges + dish.visszaigazolt + dish.fuggo -
    (dish.previous.vegleges + dish.previous.visszaigazolt + dish.previous.fuggo)
  if (delta === 0) return 'átsorolódott az oszlopok között'
  return `${delta > 0 ? '+' : ''}${n(delta)} adag a legutóbbi előrejelzés óta`
}
